import { useState, useMemo } from 'react';
import { Stack, Table, useTheme, spacing } from '@fti/design-system';
import type { Publication } from '../../../types/media-sim';

interface PublicationsTableProps {
  publications: Publication[];
  selectedId: string | null;
  onSelect: (pub: Publication) => void;
}

const REFLECTION_STATUS_CONFIG: Record<string, { label: string; colorKey: string }> = {
  current: { label: 'Current', colorKey: 'success' },
  generating: { label: 'Generating', colorKey: 'warning' },
  not_generated: { label: 'Not Generated', colorKey: 'neutral' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function PublicationsTable({ publications, selectedId, onSelect }: PublicationsTableProps) {
  const { theme } = useTheme();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return publications;
    const q = search.toLowerCase();
    return publications.filter((p) => p.publisher.toLowerCase().includes(q));
  }, [publications, search]);

  const columns = [
    {
      key: 'publisher',
      header: 'Publication',
      sortable: true,
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        const isSelected = selectedId === pub.id;
        return (
          <button
            onClick={() => onSelect(pub)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isSelected ? theme.colors.primary[700] : theme.colors.primary[500],
              fontWeight: isSelected ? 600 : 500,
              padding: 0,
              textDecoration: 'underline',
              fontSize: 'inherit',
              textAlign: 'left',
            }}
          >
            {pub.publisher}
          </button>
        );
      },
    },
    {
      key: 'article_count',
      header: 'Articles',
      width: '100px',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        return (
          <span style={{ fontSize: '0.85rem', color: pub.article_count != null ? theme.colors.text.primary : theme.colors.text.secondary }}>
            {pub.article_count != null ? pub.article_count.toLocaleString() : '—'}
          </span>
        );
      },
    },
    {
      key: 'earliest_date',
      header: 'Earliest Date',
      width: '130px',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        return (
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
            {formatDate(pub.earliest_date)}
          </span>
        );
      },
    },
    {
      key: 'latest_date',
      header: 'Latest Date',
      width: '130px',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        return (
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
            {formatDate(pub.latest_date)}
          </span>
        );
      },
    },
    {
      key: 'static_reflection_status',
      header: 'Reflections',
      width: '140px',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        const config = REFLECTION_STATUS_CONFIG[pub.static_reflection_status] || {
          label: pub.static_reflection_status || 'Unknown',
          colorKey: 'neutral',
        };
        const bgColor =
          config.colorKey === 'neutral'
            ? theme.colors.neutral[200]
            : theme.colors.semantic[config.colorKey as 'success' | 'warning']?.light || theme.colors.neutral[200];
        const textColor =
          config.colorKey === 'neutral'
            ? theme.colors.text.secondary
            : theme.colors.semantic[config.colorKey as 'success' | 'warning']?.default || theme.colors.text.secondary;

        return (
          <span
            style={{
              display: 'inline-block',
              padding: `2px ${spacing.xs}`,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '4px',
              backgroundColor: bgColor,
              color: textColor,
            }}
          >
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'static_reflection_updated_at',
      header: 'Last Generated',
      width: '150px',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        return (
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
            {formatDate(pub.static_reflection_updated_at)}
          </span>
        );
      },
    },
  ];

  return (
    <Stack gap="sm">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search publications..."
        style={{
          width: '100%',
          padding: `${spacing.xs} ${spacing.sm}`,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '4px',
          backgroundColor: theme.colors.background.paper,
          color: theme.colors.text.primary,
          fontSize: '0.9rem',
        }}
      />
      <Table
        data={filtered as unknown as Record<string, unknown>[]}
        columns={columns}
      />
    </Stack>
  );
}
